import type {
  TransitionPresentation,
  TransitionPresentationComponentProps,
} from "@remotion/transitions";
import type React from "react";
import { AbsoluteFill, random, useVideoConfig } from "remotion";
import { isTransitionIdle } from "./transition-idle.ts";

// Adapted from https://remocn.dev/docs/transitions/glitch-cut. Only remocn's
// CSS fallback is kept: Remotion's renderer enables html-in-canvas, so the
// WebGL shader would render on the server while users preview the fallback.

export type GlitchCutProps = {
  intensity?: number;
  slices?: number;
  rgbSplit?: number;
};

const DEFAULTS = {
  intensity: 1,
  slices: 24,
  rgbSplit: 1,
};

const STEPS = 22;

const bandCut = (band: number) =>
  0.5 + (random(`glitch-cut-handoff-${band}`) - 0.5) * 0.32;

const GlitchCutPresentation: React.FC<
  TransitionPresentationComponentProps<GlitchCutProps>
> = ({
  children,
  presentationProgress,
  presentationDirection,
  passedProps,
}) => {
  const {
    intensity = DEFAULTS.intensity,
    slices = DEFAULTS.slices,
    rgbSplit = DEFAULTS.rgbSplit,
  } = passedProps;
  const { width } = useVideoConfig();
  const entering = presentationDirection === "entering";
  const p = presentationProgress;

  if (isTransitionIdle(presentationDirection, p)) {
    return (
      <AbsoluteFill>
        <AbsoluteFill>{children}</AbsoluteFill>
        {null}
      </AbsoluteFill>
    );
  }
  const energy = Math.max(0, Math.sin(p * Math.PI)) ** 0.7;

  const clock = Math.floor(p * STEPS);
  const bands = Math.max(2, Math.min(6, Math.round(slices / 5)));
  const height = 100 / bands;
  const visible = Array.from(
    { length: bands },
    (_, band) => p >= bandCut(band) === entering,
  );
  const shownCount = visible.filter(Boolean).length;

  // Each side shifts on its own seed, so the interleaved bands of the two
  // scenes still tear against each other.
  const maxShift = 26 * intensity * energy;
  const shift =
    (random(`glitch-cut-shift-${clock}-${presentationDirection}`) - 0.5) *
    2 *
    maxShift;
  // Zoom in just enough that the largest possible shift never reveals an edge.
  const overscan = 1 + (2 * maxShift) / width;

  // Local patch: render `children` exactly once in a stable tree position.
  // Upstream cloned the scene per band and swapped trees at the edges, which
  // remounted every <Video> and stalled the player. The black backing fills the
  // strip the jitter exposes, so Studio's loading indicator can't show through.
  const glitching = energy > 0.002;

  return (
    <AbsoluteFill
      style={{
        opacity: shownCount === 0 ? 0 : 1,
        clipPath:
          shownCount > 0 && shownCount < bands
            ? bandsClipPath(visible)
            : undefined,
        backgroundColor: glitching ? "black" : undefined,
      }}
    >
      <AbsoluteFill
        style={
          glitching
            ? { translate: `${shift}px`, scale: `${overscan}` }
            : undefined
        }
      >
        {children}
      </AbsoluteFill>
      {glitching ? (
        <AbsoluteFill style={{ pointerEvents: "none" }}>
          {visible.map((shown, band) =>
            shown ? (
              <div
                key={band}
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: `${band * height}%`,
                  height: `${height}%`,
                  background:
                    band % 2 === 0 ? "rgb(255, 42, 90)" : "rgb(0, 224, 255)",
                  mixBlendMode: "screen",
                  opacity:
                    rgbSplit *
                    0.2 *
                    energy *
                    random(`glitch-cut-tint-${clock}-${band}`),
                }}
              />
            ) : null,
          )}
        </AbsoluteFill>
      ) : null}
    </AbsoluteFill>
  );
};

// Union of the visible bands as one polygon; adjacent bands are merged so no
// seams appear between them.
function bandsClipPath(visible: boolean[]): string {
  const height = 100 / visible.length;
  const points: string[] = [];
  let runStart: number | null = null;

  visible.forEach((shown, band) => {
    if (shown && runStart === null) {
      runStart = band;
    }
    const runEnds =
      runStart !== null && (!shown || band === visible.length - 1);
    if (runEnds) {
      const top = runStart! * height;
      const bottom = (shown ? band + 1 : band) * height;
      points.push(
        `0% ${top}%`,
        `100% ${top}%`,
        `100% ${bottom}%`,
        `0% ${bottom}%`,
      );
      runStart = null;
    }
  });

  return `polygon(${points.join(", ")})`;
}

export function glitchCut(
  props: GlitchCutProps = {},
): TransitionPresentation<GlitchCutProps> {
  return { component: GlitchCutPresentation, props };
}
